import type { QueryClient } from '@tanstack/react-query'

import { qk } from '@/api/query-keys'
import type { DashboardDataSource, Widget } from '@/api/types'
import {
  inOrder,
  withDataSource,
  withWidget,
  withoutWidget,
} from '@/features/dashboard/cache'
import type { RealtimeClient, RealtimeContext } from '../client'
import type { RealtimeMessage } from '../protocol'

/**
 * The dashboard handlers.
 *
 * Deferred out of M4 because there was no dashboard for them to patch. They
 * arrive here with one, and they are the last of the sixty-seven.
 *
 * All of them are broadcast to the dashboard's own group rather than to the
 * workspace, so they reach a tab only while it has that dashboard open -- see
 * `useRealtimePage`. Every one of them names its dashboard for that reason, and
 * there is no case of an event arriving for a board nobody is looking at.
 *
 * Ported from `web-frontend/modules/dashboard/realtime.js`.
 */

function patchWidgets(
  queryClient: QueryClient,
  dashboardId: number,
  update: (held: readonly Widget[]) => Widget[],
): void {
  queryClient.setQueryData<Widget[]>(qk.widgets(dashboardId), (held) =>
    held === undefined ? held : update(held),
  )
}

interface WidgetEvent {
  dashboardId: number
  widget: Widget
}

function readWidget(data: RealtimeMessage): WidgetEvent | null {
  const { dashboard_id: dashboardId, widget } = data as {
    dashboard_id?: number
    widget?: Widget
  }
  if (typeof dashboardId !== 'number' || widget === undefined) return null
  return { dashboardId, widget }
}

export function registerDashboardHandlers(client: RealtimeClient): () => void {
  const off: (() => void)[] = []
  const on = (
    type: string,
    handle: (queryClient: QueryClient, data: RealtimeMessage) => void,
  ) => {
    off.push(
      client.on(type, (context: RealtimeContext, data: RealtimeMessage) => {
        handle(context.queryClient, data)
      }),
    )
  }

  on('widget_created', (queryClient, data) => {
    const held = readWidget(data)
    if (held === null) return

    patchWidgets(queryClient, held.dashboardId, (widgets) =>
      withWidget(widgets, held.widget),
    )
    // Creating a widget creates the data source it reads, server-side, and the
    // event carries only the widget. Without this the new card arrives saying
    // its data source is gone, which it is not -- it is unfetched.
    void queryClient.invalidateQueries({ queryKey: qk.dataSources(held.dashboardId) })
  })

  on('widget_updated', (queryClient, data) => {
    const held = readWidget(data)
    if (held === null) return

    patchWidgets(queryClient, held.dashboardId, (widgets) =>
      withWidget(widgets, held.widget),
    )
  })

  on('widget_deleted', (queryClient, data) => {
    const held = readWidget(data)
    if (held === null) return

    patchWidgets(queryClient, held.dashboardId, (widgets) =>
      withoutWidget(widgets, held.widget.id),
    )
    void queryClient.invalidateQueries({ queryKey: qk.dataSources(held.dashboardId) })

    // The answer the deleted data source gave is dropped rather than marked
    // stale. Nothing is subscribed to it any more, so an invalidation would
    // leave the number cached under an id the server is free to hand to the
    // next data source somebody makes.
    const sourceId = held.widget.data_source_id
    if (typeof sourceId === 'number') {
      queryClient.removeQueries({
        queryKey: qk.dataSourceResult(held.dashboardId, sourceId),
      })
    }
  })

  on('widgets_reordered', (queryClient, data) => {
    const { dashboard_id: dashboardId, order } = data as {
      dashboard_id?: number
      order?: number[]
    }
    if (typeof dashboardId !== 'number' || !Array.isArray(order)) return

    patchWidgets(queryClient, dashboardId, (widgets) => inOrder(widgets, order))
  })

  on('data_source_updated', (queryClient, data) => {
    const { dashboard_id: dashboardId, data_source: source } = data as {
      dashboard_id?: number
      data_source?: DashboardDataSource
    }
    if (typeof dashboardId !== 'number' || source === undefined) return

    queryClient.setQueryData<DashboardDataSource[]>(
      qk.dataSources(dashboardId),
      (held) => (held === undefined ? held : withDataSource(held, source)),
    )
    // The question changed, so the answer is wrong. Invalidated rather than
    // patched, because the event says what was asked and not what it came to.
    void queryClient.invalidateQueries({
      queryKey: qk.dataSourceResult(dashboardId, source.id),
    })
  })

  return () => {
    for (const remove of off) remove()
  }
}
